import React, { useState, useRef } from 'react';
import SortIcon from '@/shared/components/dataDisplay/SortIcon';
import { shouldShowColumn } from '../../../shared/lib/settlementHelpers';
import { TABLE_HEADERS } from '../constants/tableHeaders';
import HeaderTooltip from '../../../shared/components/HeaderTooltip';

interface TableHeaderProps {
    isSupplierUser: boolean;
    sortConfig: { columnKey: string | null; direction: 'asc' | 'desc' | null };
    handleSort: (key: string) => void;
}

const TableHeader: React.FC<TableHeaderProps> = ({ isSupplierUser, sortConfig, handleSort }) => {
    const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
    const headerRefs = useRef<{ [key: string]: HTMLTableCellElement | null }>({});

    return (
        <thead>
        <tr>
            {TABLE_HEADERS.map(header => {
                if (!shouldShowColumn(header.columnIndex, isSupplierUser)) return null;

                const stickyStyle = 'sticky' in header && header.sticky ? {
                    position: 'sticky' as const,
                    top: 0,
                    left: header.left,
                    zIndex: 3,
                } : {};

                const isSorted = sortConfig.columnKey === header.key;
                const direction = isSorted ? sortConfig.direction : null;
                const hasTooltip = 'tooltip' in header && header.tooltip;

                return (
                    <th
                        key={header.key}
                        ref={el => { headerRefs.current[header.key] = el; }}
                        style={{
                            width: header.width,
                            minWidth: header.width,
                            ...stickyStyle
                        }}
                        className={`${header.sortable ? 'sortable-header' : ''} ${isSorted ? 'sorted' : ''} ${hasTooltip ? 'has-tooltip' : ''}`}
                        onClick={() => header.sortable && handleSort(header.key)}
                        onMouseEnter={() => hasTooltip && setActiveTooltip(header.key)}
                        onMouseLeave={() => setActiveTooltip(null)}
                    >
                            <span className="header-content">
                                {header.label}
                                {hasTooltip && (
                                    <i className="fas fa-info-circle tooltip-icon"></i>
                                )}
                                {header.sortable && <SortIcon direction={direction} />}
                            </span>
                        {hasTooltip && (
                            <HeaderTooltip
                                content={header.tooltip as string}
                                targetRef={{ current: headerRefs.current[header.key] }}
                                visible={activeTooltip === header.key}
                                tooltipClassName="settlement-header-tooltip"
                            />
                        )}
                    </th>
                );
            })}
        </tr>
        </thead>
    );
};

export default TableHeader;
